import React, { useState } from "react";
import { Modal, Typography, Button, Divider, Space, Checkbox, Alert } from "antd";
import { FileTextOutlined, CheckCircleOutlined, ExclamationCircleOutlined, HomeOutlined, MailOutlined, PhoneOutlined, InfoCircleOutlined } from "@ant-design/icons";
import "../../../scss/TermsAndConditionsModal.css"

const { Title, Paragraph, Text } = Typography;

const TermsAndConditionsModal = ({ open, onAccept, onCancel }) => {
  const [accepted, setAccepted] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10;
    setScrolledToBottom(isAtBottom);
  };

  const termsContent = [
    {
      title: "1. Uso del Sitio Web",
      content: "Epno.com.mx es una plataforma de órdenes en línea y marketplace diseñada para facilitar la compra de productos y la contratación de servicios industriales. Al usar este sitio web, usted se compromete a utilizarlo únicamente para fines legales y de acuerdo con estos términos."
    },
    {
      title: "2. Propiedad Intelectual",
      content: "Todo el contenido, diseños, logotipos, marcas registradas, textos, imágenes y software disponibles en epno.com.mx son propiedad exclusiva de EPNO Corporation o de sus licenciantes, y están protegidos por las leyes de propiedad intelectual aplicables. Queda prohibida su reproducción, distribución, modificación o uso sin autorización previa y por escrito."
    },
    {
      title: "3. Marketplace y Proveedores Externos",
      content: "epno.com.mx actúa únicamente como una plataforma tecnológica que conecta compradores con proveedores externos. EPNO Corporation no fabrica, vende ni entrega directamente productos o servicios. Todas las transacciones, condiciones comerciales y responsabilidades corresponden exclusivamente a los proveedores y compradores involucrados."
    },
    {
      title: "4. Limitación de Responsabilidad",
      content: "EPNO Corporation no será responsable por daños, pérdidas o perjuicios derivados de transacciones realizadas con proveedores externos, incluyendo, sin limitarse a, la calidad de productos, incumplimientos, retrasos en entrega o servicios postventa. Cualquier controversia deberá resolverse directamente entre el comprador y el proveedor."
    },
    {
      title: "5. Pagos y Reembolsos",
      content: "Los pagos se procesan conforme a las políticas establecidas por cada proveedor y a través de la pasarela de pago Openpay. EPNO Corporation no es responsable por disputas de pago, contracargos o procesos de reembolso derivados de dichas transacciones."
    },
    {
      title: "6. Política de Privacidad",
      content: "El tratamiento de los datos personales se realiza conforme a nuestro Aviso de Privacidad, disponible en el sitio web. Al utilizar la plataforma, el usuario consiente la recopilación, uso y protección de sus datos conforme a dicho aviso."
    },
    {
      title: "7. Modificaciones a los Términos",
      content: "EPNO Corporation se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento. Las modificaciones serán publicadas en esta misma sección y entrarán en vigor a partir de su publicación."
    },
    {
      title: "8. Ley Aplicable y Jurisdicción",
      content: "Estos términos se rigen por las leyes vigentes de los Estados Unidos Mexicanos. Cualquier controversia derivada del uso de epno.com.mx se someterá a la jurisdicción de los tribunales competentes en México."
    }
  ];

  const contactInfo = [
    { icon: <HomeOutlined />, text: "Municipio Libre 3529-D, Tres Hermanos Areco Cd. Juárez, Chihuahua, México. C.P. 32385" },
    { icon: <MailOutlined />, text: "soporte@epno.com.mx" },
    { icon: <PhoneOutlined />, text: "656 831 5340" }
  ];

  return (
    <Modal
      title={
        <div className="terms-modal-header">
          <FileTextOutlined className="terms-modal-icon" />
          <span>Términos y Condiciones</span>
        </div>
      }
      open={open}
      onCancel={onCancel}
      width={800}
      footer={[
        <div key="footer" className="terms-modal-footer">
          <div className="terms-acceptance">
            <Checkbox
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              disabled={!scrolledToBottom}
            >
              <span className="acceptance-text">
                He leído y acepto los Términos y Condiciones
              </span>
            </Checkbox>
            {!scrolledToBottom && (
              <div className="scroll-hint">
                <ExclamationCircleOutlined />
                <span>Desplázate hasta el final para aceptar</span>
              </div>
            )}
          </div>
          <Space>
            <Button onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              type="primary"
              onClick={onAccept}
              disabled={!accepted}
              icon={<CheckCircleOutlined />}
            >
              Aceptar y Continuar
            </Button>
          </Space>
        </div>
      ]}
      className="terms-modal"
    >
      <Alert
        message="Importante"
        description="Por favor, lea detenidamente los siguientes términos y condiciones antes de proceder con su compra."
        type="info"
        showIcon
        className="terms-alert"
      />

      <div className="terms-content" onScroll={handleScroll}>
        <div className="terms-intro">
          <Title level={4} className="terms-title">
            <span className="company-name">EPNO Corporation</span>
          </Title>
          <Paragraph className="welcome-text">
            Bienvenido a epno.com.mx. Al acceder o utilizar nuestra plataforma, usted acepta cumplir y estar sujeto a los siguientes Términos y Condiciones. Si no está de acuerdo con ellos, le recomendamos no utilizar el sitio.
          </Paragraph>
        </div>

        <Divider className="terms-divider" />

        <div className="terms-sections">
          {termsContent.map((section, index) => (
            <div key={index} className="terms-section">
              <Title level={5} className="section-title">
                <span className="section-number">{section.title.split(" ")[0]}</span>
                <span className="section-title-text">{section.title.substring(3)}</span>
              </Title>
              <Paragraph className="section-content">
                {section.content}
              </Paragraph>
              {index < termsContent.length - 1 && <Divider className="section-divider" />}
            </div>
          ))}
        </div>

        <div className="contact-section">
          <Title level={4} className="contact-title">
            <InfoCircleOutlined /> Contacto
          </Title>
          <Paragraph className="contact-description">
            Para cualquier duda, aclaración o comentario relacionado con estos Términos y Condiciones, puede contactarnos en:
          </Paragraph>
          <div className="contact-details">
            {contactInfo.map((info, index) => (
              <div key={index} className="contact-item">
                <span className="contact-icon">{info.icon}</span>
                <span className="contact-text">{info.text}</span>
              </div>
            ))}
          </div>
        </div>

        {!scrolledToBottom && (
          <div className="scroll-indicator">
            <div className="scroll-pulse"></div>
            <span>Continúa desplazando para ver todos los términos</span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TermsAndConditionsModal;